import React, { useState, useMemo } from 'react';
import InsightCard from './InsightCard';
import InsightChart from './InsightChart';
import InsightPieChart from './InsightPieChart';
import { useMetrics } from '@/store/metrics/hooks';
import type { ChartDataPoint, MetricType } from '@/shared/types/metrics';
import { DateTime } from 'luxon';

const METRIC_CONFIGS = [
  {
    id: 'attendance' as MetricType,
    label: 'Attendance',
    subLabel: 'Weekly attendance (Adults & Kids)',
    color: '#6366f1', // Indigo-500
  },
  {
    id: 'serving' as MetricType,
    label: 'Team Serving',
    subLabel: 'Weekly team serving',
    color: '#0ea5e9', // Sky-500
  },
  {
    id: 'members' as MetricType,
    label: 'Members',
    subLabel: 'Weekly members overview',
    color: '#14b8a6', // Teal-500
  },
];

const InsightMetrics = () => {
  const [activeMetric, setActiveMetric] = useState<MetricType>('attendance');
  const { metrics: attendanceMetrics, loading: attendanceLoading } =
    useMetrics('attendance');
  const { metrics: servingMetrics, loading: servingLoading } =
    useMetrics('serving');
  const { metrics: membersMetrics, loading: membersLoading } =
    useMetrics('members');

  const metrics = useMemo(() => {
    return {
      attendance: attendanceMetrics,
      serving: servingMetrics,
      members: membersMetrics,
    } satisfies Record<MetricType, ChartDataPoint[]>;
  }, [attendanceMetrics, servingMetrics, membersMetrics]);

  const loading = useMemo(() => {
    return {
      attendance: attendanceLoading,
      serving: servingLoading,
      members: membersLoading,
    } satisfies Record<MetricType, boolean>;
  }, [attendanceLoading, servingLoading, membersLoading]);

  const metricsData = useMemo(() => {
    return METRIC_CONFIGS.map((config) => {
      const metricValues = metrics[config.id] || [];
      const currentValue = metricValues[metricValues.length - 1]?.value || 0;
      const previousValue = metricValues[metricValues.length - 2]?.value || 0;

      const percentage =
        previousValue === 0
          ? 0
          : Math.round(((currentValue - previousValue) / previousValue) * 100);

      return {
        ...config,
        value: currentValue,
        percentage,
      };
    });
  }, [metrics]);

  const parseDate = (value: string) => {
    return DateTime.fromFormat(value, 'yyyy-MM-dd').toJSDate();
  };

  const chartData = useMemo(() => {
    return (
      metrics[activeMetric].map((metric) => ({
        period: parseDate(metric.period),
        value: metric.value,
      })) || []
    );
  }, [metrics, activeMetric]);

  const pieChartEnabled = false;

  const pieChartData = useMemo(() => {
    if (!pieChartEnabled) {
      return [];
    }

    return metricsData.map((metric) => ({
      name: metric.label,
      value: metric.value,
      color: metric.color,
    }));
  }, [metricsData, pieChartEnabled]);

  return (
    <div>
      <p className='px-3 text-sm font-medium leading-5'>Insights</p>

      <div className='mx-auto p-3'>
        <div className='mb-4 grid grid-cols-3 gap-3 sm:mb-8 sm:gap-3'>
          {metricsData.map((metric) => (
            <InsightCard
              key={metric.id}
              label={metric.label}
              value={metric.value}
              percentage={metric.percentage}
              isActive={activeMetric === metric.id}
              isLoading={loading[metric.id]}
              onClick={() => setActiveMetric(metric.id)}
            />
          ))}
        </div>

        <div className='grid grid-cols-1 gap-6 lg:grid-cols-3'>
          <div className='rounded-2xl bg-white p-3 ring-1 ring-slate-200 md:p-6 lg:col-span-2'>
            <div className='flex flex-col items-center justify-center'>
              <p className='mb-1 text-xl font-semibold text-gray-800'>
                {metricsData.find((m) => m.id === activeMetric)?.label} Trends
              </p>
              <p className='text-md font-medium text-gray-500'>
                {metricsData.find((m) => m.id === activeMetric)?.subLabel}
              </p>
            </div>

            {loading[activeMetric] ? (
              <div className='flex h-64 items-center justify-center'>
                <div className='h-8 w-8 animate-spin rounded-full border-b-2 border-gray-900' />
              </div>
            ) : (
              <InsightChart data={chartData} />
            )}
          </div>

          {pieChartEnabled && (
            <div className='rounded-2xl bg-white p-3 ring-1 ring-slate-200 md:p-6'>
              <p className='mb-4 text-center text-lg font-semibold text-slate-800'>
                Metrics Overview
              </p>
              {loading ? (
                <div className='flex h-64 items-center justify-center'>
                  <div className='h-8 w-8 animate-spin rounded-full border-b-2 border-gray-900' />
                </div>
              ) : (
                <InsightPieChart data={pieChartData} />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InsightMetrics;
